import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, Modal, Field, Button, inr } from "../components/UI";
import { Plus, Trash2, Target, TrendingUp, IndianRupee } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", email: "", password: "", role: "staff", monthly_sales_target: 0, monthly_collection_target: 0 };

export default function Users() {
  const [list, setList] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [open, setOpen] = useState(false);
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [activeUser, setActiveUser] = useState(null);
  const [form, setForm] = useState(blank);
  const [targets, setTargets] = useState({ monthly_sales_target: 0, monthly_collection_target: 0 });

  const load = async () => {
    try {
      const [u, lb] = await Promise.all([api.get("/users"), api.get("/staff/leaderboard").catch(() => ({ data: [] }))]);
      setList(u.data); setLeaderboard(lb.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...form, monthly_sales_target: Number(form.monthly_sales_target), monthly_collection_target: Number(form.monthly_collection_target) });
      toast.success("User created");
      setOpen(false); setForm(blank); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openTargets = (u) => {
    setActiveUser(u);
    setTargets({
      monthly_sales_target: u.monthly_sales_target || 0,
      monthly_collection_target: u.monthly_collection_target || 0,
    });
    setTargetsOpen(true);
  };

  const saveTargets = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${activeUser.id}/targets`, {
        monthly_sales_target: Number(targets.monthly_sales_target),
        monthly_collection_target: Number(targets.monthly_collection_target),
      });
      toast.success("Targets updated");
      setTargetsOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-6xl">
      <PageHeader
        title="Team & Targets"
        subtitle="Manage staff accounts and assign monthly sales / collection targets"
        action={<Button onClick={() => { setForm(blank); setOpen(true); }} data-testid="add-user-btn"><Plus size={16}/>Add User</Button>}
      />

      {/* Leaderboard - this month progress */}
      {leaderboard.length > 0 && (
        <div className="card-flat p-5 mb-6" data-testid="leaderboard">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={18} className="text-[#C1121F]"/>
            <h3 className="font-display font-bold text-lg">This Month at a Glance</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {leaderboard.map((row) => (
              <div key={row.user_id} className="p-3 border border-[#E5E7EB] rounded">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display font-bold text-sm">{row.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{row.role}</div>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  <div>
                    <div className="flex justify-between text-[10px] uppercase tracking-wider text-[#475569] font-semibold">
                      <span>Sales</span><span>{row.sales_pct}%</span>
                    </div>
                    <div className="h-1.5 bg-[#F1F5F9] rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-[#C1121F] transition-all" style={{ width: `${Math.min(100, row.sales_pct)}%` }} />
                    </div>
                    <div className="text-[10px] text-[#475569] mt-0.5 font-mono-num">{inr(row.sales_achieved)} / {inr(row.sales_target)}</div>
                  </div>
                  <div>
                    <div className="flex justify-between text-[10px] uppercase tracking-wider text-[#475569] font-semibold">
                      <span>Collection</span><span>{row.collection_pct}%</span>
                    </div>
                    <div className="h-1.5 bg-[#F1F5F9] rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-green-600 transition-all" style={{ width: `${Math.min(100, row.collection_pct)}%` }} />
                    </div>
                    <div className="text-[10px] text-[#475569] mt-0.5 font-mono-num">{inr(row.collection_achieved)} / {inr(row.collection_target)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-flat overflow-hidden">
        <table className="crm-table" data-testid="users-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th className="text-right">Sales Target</th><th className="text-right">Collection Target</th><th>Joined</th><th></th></tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td>
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[#C1121F] text-white flex items-center justify-center font-display font-bold text-sm">{u.name.charAt(0).toUpperCase()}</div>
                    <span className="font-semibold">{u.name}</span>
                  </div>
                </td>
                <td className="font-mono-num text-sm">{u.email}</td>
                <td>
                  <span className={`badge ${u.role === "admin" ? "badge-printing" : "badge-pending"}`}>{u.role}</span>
                </td>
                <td className="text-right font-mono-num">{inr(u.monthly_sales_target || 0)}</td>
                <td className="text-right font-mono-num">{inr(u.monthly_collection_target || 0)}</td>
                <td className="text-xs text-[#475569]">{u.created_at.slice(0, 10)}</td>
                <td className="text-right">
                  <button onClick={() => openTargets(u)} data-testid={`set-targets-${u.id}`} className="p-1.5 text-[#475569] hover:text-[#C1121F]" title="Set targets"><Target size={15}/></button>
                  {u.role !== "admin" && (
                    <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} className="p-1.5 text-[#475569] hover:text-red-600"><Trash2 size={15}/></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Team Member" maxWidth="max-w-xl">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" required>
              <input data-testid="user-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-base" />
            </Field>
            <Field label="Email" required>
              <input data-testid="user-email" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="input-base" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Password" required>
              <input data-testid="user-password" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="input-base" />
            </Field>
            <Field label="Role" required>
              <select data-testid="user-role" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="input-base">
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#E5E7EB]">
            <Field label="Monthly Sales Target (₹)">
              <input data-testid="user-sales-target" type="number" min="0" value={form.monthly_sales_target} onChange={(e) => setForm({...form, monthly_sales_target: e.target.value})} className="input-base" />
            </Field>
            <Field label="Monthly Collection Target (₹)">
              <input data-testid="user-collection-target" type="number" min="0" value={form.monthly_collection_target} onChange={(e) => setForm({...form, monthly_collection_target: e.target.value})} className="input-base" />
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-user-btn">Create User</Button>
          </div>
        </form>
      </Modal>

      <Modal open={targetsOpen} onClose={() => setTargetsOpen(false)} title={`Set Targets — ${activeUser?.name || ""}`}>
        <form onSubmit={saveTargets} className="space-y-4">
          <Field label="Monthly Sales Target (₹)">
            <div className="relative">
              <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input data-testid="targets-sales" type="number" min="0" value={targets.monthly_sales_target} onChange={(e) => setTargets({...targets, monthly_sales_target: e.target.value})} className="input-base pl-8" />
            </div>
          </Field>
          <Field label="Monthly Collection Target (₹)">
            <div className="relative">
              <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input data-testid="targets-collection" type="number" min="0" value={targets.monthly_collection_target} onChange={(e) => setTargets({...targets, monthly_collection_target: e.target.value})} className="input-base pl-8" />
            </div>
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setTargetsOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-targets-btn">Save Targets</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
